import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../../components/Navbar';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [userType, setUserType] = useState('business');
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const endpoints = {
    business: '/api/auth/login/business',
    agent: '/api/auth/login/agent',
    super_admin: '/api/auth/login/super-admin'
  };

  const redirects = {
    business: '/dashboard',
    agent: '/agent',
    main_agent: '/agent',
    super_admin: '/super-admin'
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(endpoints[userType], form);
      const role = data.data.role; // always use role from backend — never guess from userType
      if (!role) throw new Error('Invalid login response');
      login(data.data, role, data.token);
      toast.success('Welcome back!');
      navigate(redirects[role] || '/dashboard');
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed';
      const status = err.response?.data?.status;
      if (status === 'pending_payment') {
        toast.error('Please complete payment first');
        navigate('/payment');
      } else if (status === 'pending_approval') {
        toast('Payment is under review. We will notify you shortly.', { icon: '⏳' });
      } else if (status === 'suspended') {
        toast.error('Account suspended. Please renew your subscription.');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    try {
      await axios.post('/api/auth/forgot-password', { email: forgotEmail });
      toast.success('Request sent! Admin will contact you shortly.');
      setForgotMode(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Request failed');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ marginBottom: 32 }}><Logo /></div>
      <div style={{ width: '100%', maxWidth: 440, background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 16, overflow: 'hidden' }}>
        {/* User type tabs */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: '#111', borderBottom: '1px solid #1e1e1e' }}>
          {[['business', 'Business'], ['agent', 'Agent'], ['super_admin', 'Admin']].map(([t, l]) => (
            <button key={t} onClick={() => setUserType(t)} style={{ padding: '12px 8px', background: 'transparent', border: 'none', borderBottom: `2px solid ${userType === t ? '#00d55b' : 'transparent'}`, color: userType === t ? '#00d55b' : '#555', fontSize: 13, fontWeight: userType === t ? 600 : 400, cursor: 'pointer' }}>
              {l}
            </button>
          ))}
        </div>

        <div style={{ padding: 32 }}>
          {!forgotMode ? (
            <>
              <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
                {userType === 'business' ? 'Business Login' : userType === 'agent' ? 'Agent Login' : 'Admin Login'}
              </h2>
              <p style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>
                {userType === 'business' ? 'Access your business dashboard' : userType === 'agent' ? 'Access your agent workspace' : 'Super admin access only'}
              </p>
              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Email Address</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@company.com" required />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Password</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Your password" required />
                </div>
                {userType === 'agent' && (
                  <div style={{ textAlign: 'right' }}>
                    <button type="button" onClick={() => setForgotMode(true)} style={{ background: 'none', border: 'none', color: '#00d55b', fontSize: 12, cursor: 'pointer' }}>Forgot password?</button>
                  </div>
                )}
                <button type="submit" disabled={loading} style={{ padding: '12px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 15, marginTop: 4, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'Logging in...' : 'Login →'}
                </button>
              </form>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>Forgot Password</h2>
              <p style={{ color: '#555', fontSize: 13 }}>Enter your email. Your request will be sent to the super admin who will reset your password manually.</p>
              <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" />
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setForgotMode(false)} style={{ flex: 1, padding: '10px', background: '#111', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={handleForgot} style={{ flex: 2, padding: '10px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Send Request</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {userType === 'business' && (
        <p style={{ color: '#333', fontSize: 13, marginTop: 20 }}>
          Don't have an account? <Link to="/register" style={{ color: '#00d55b' }}>Register here</Link>
        </p>
      )}
    </div>
  );
}